import type { ConversationRow } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { guestDisplayName, pluralizeRu } from '@/shared/lib/format'
import { useHoverIntent } from '@/shared/lib/use-hover-intent'
import { messengerLabel } from '../model/messenger'
import { MessengerAvatar } from './MessengerAvatar'
import s from './ConversationListItem.module.css'

/**
 * Приписка стороны в превью. У гостя её нет: его чат, его реплика — приписка была бы шумом на
 * большинстве строк. Набор закрытый (`ConversationRow.previewFrom`), поэтому запись полная.
 */
const PREVIEW_PREFIX: Record<ConversationRow['previewFrom'], string> = {
  guest: '',
  manager: 'Мы: ',
  ai: 'ИИ: ',
  system: 'Система: ',
}

type ConversationListItemProps = {
  conversation: ConversationRow
  active?: boolean
  onClick: (conversationId: string) => void
  onPrefetch: (conversationId: string) => void
  className?: string
}

export function ConversationListItem({
  conversation,
  active = false,
  onClick,
  onPrefetch,
  className,
}: ConversationListItemProps) {
  const hover = useHoverIntent(() => onPrefetch(conversation.id))

  return (
    <button
      type="button"
      className={cn(s.root, className)}
      data-active={active}
      aria-pressed={active}
      onClick={() => onClick(conversation.id)}
      onPointerEnter={hover.onPointerEnter}
      onPointerLeave={hover.onPointerLeave}
    >
      <span className={s.avatarWrap}>
        <MessengerAvatar messenger={conversation.channel} size={36} />
      </span>

      <span className={s.main}>
        <span className={s.head}>
          <span className={s.name}>{guestDisplayName(conversation.guestName)}</span>
          <span className={s.channel}>{messengerLabel(conversation.channel).toUpperCase()}</span>
        </span>
        {/* Кто написал последним — приписка перед текстом. Без неё строка не отвечает на главный
            вопрос разбора: «Уже в пути» от гостя и «Хорошо, ждём» от нас выглядят одинаково.
            «Мы», а не «Вы»: ящик общий, и ответить мог другой менеджер. */}
        <span className={s.preview}>
          {PREVIEW_PREFIX[conversation.previewFrom] ? (
            <span className={s.previewFrom}>{PREVIEW_PREFIX[conversation.previewFrom]}</span>
          ) : null}
          {conversation.preview}
        </span>
        {/* Диалог ведёт помощник — у списка есть фильтр «ИИ», и без этой строки он отбирает
            внешне такие же строки, то есть не отвечает, чем отобранное отличается. */}
        {conversation.mode === 'ai' ? <span className={s.mode}>Отвечает ИИ</span> : null}
      </span>

      <span className={s.meta}>
        <span className={s.time}>{conversation.timeLabel}</span>
        {/* ⚠️ Это НЕ «непрочитано»: столько гостевых сообщений пришло после нашего последнего
            ответа (7.1). Отметки «менеджер прочитал» в системе нет вовсе, а `read_at` означает
            обратное — расписку гостя о нашем сообщении. Поэтому и число, а не точка: «ждёт
            ответа» — величина счётная, и по ней разбирают очередь. */}
        {conversation.unanswered > 0 ? (
          <span className={s.unanswered}>
            {conversation.unanswered}
            <span className={s.srOnly}>
              {` ${pluralizeRu(conversation.unanswered, ['сообщение ждёт', 'сообщения ждут', 'сообщений ждут'])} ответа`}
            </span>
          </span>
        ) : null}
      </span>
    </button>
  )
}
